import i18next from 'i18next'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { EntityState } from '../../../../api/models/apiTypes.model'
import { apiSaveUserData } from '../../../../api/services/user/user'
import { ApiSaveUserDataPostData } from '../../../../api/services/user/user.types'
import { apiFetchUsers } from '../../../../api/services/users/users'
import { ApiFetchUsersResponse } from '../../../../api/services/users/users.types'
import { State, ThunkErrorObject } from '../../../store.types'
import { changeUserDetails } from '../ApiUserDetailsSlice/ApiUserDetailsSlice'
import {
  ApiUsersState,
  ApiUserState,
  ChangeUserStatePayload,
  GetUsersPayload
} from './ApiUsersSlice.types'

const initialState: ApiUsersState = {
  ApiUsersStates: []
}

export const getUsers = createAsyncThunk<
  ApiFetchUsersResponse,
  GetUsersPayload,
  { rejectValue: ThunkErrorObject }
>('apiUsers/fetchUsers', async ({ userToken, windowId }, thunkApi) => {
  try {
    const serverPath = (thunkApi.getState() as State).user.serverUrl
    const response = await apiFetchUsers(userToken, serverPath)
    if (!response.data.ok) {
      toast.error(i18next.t('errors.an_error_has_ocurred'))
      return thunkApi.rejectWithValue({
        message: 'errors.an_error_has_ocurred',
        windowId
      })
    }
    return response.data
  } catch (error) {
    toast.error(i18next.t('errors.an_error_has_ocurred'))
    return thunkApi.rejectWithValue({
      message: 'errors.an_error_has_ocurred',
      windowId
    })
  }
})

export const changeUserStatus = createAsyncThunk(
  'apiUsers/changeUserStatus',
  async (
    {
      status,
      windowId,
      userId,
      userToken,
      serverPath
    }: {
      status: boolean
      windowId: string
      userId: string
      userToken: string
      serverPath: string
    },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as State
      const windowState = state.apiUsers.ApiUsersStates.find(
        (apiUserState) => apiUserState.windowId === windowId
      )
      if (windowState == null) {
        return
      }
      const newUserAuxList = [...windowState.userAuxList]
      const userToDisableId = newUserAuxList.findIndex(
        (user) => user.id === userId
      )
      if (userToDisableId === -1) {
        return
      }
      const disabledUser = {
        ...newUserAuxList[userToDisableId],
        active: status,
        entityState: EntityState.Modified
      }
      newUserAuxList[userToDisableId] = disabledUser

      const response = await apiSaveUserData(
        {
          userToken,
          userAuxList: [disabledUser]
        },
        serverPath
      )

      const resData = response.data

      if (!resData.ok) {
        toast.error(i18next.t('errors.an_error_has_ocurred'))
        return thunkApi.rejectWithValue({
          message: 'errors.an_error_has_ocurred'
        } as ThunkErrorObject)
      }
      if (status) {
        toast.success(i18next.t('alerts.enabled_user'))
      } else {
        toast.success(i18next.t('alerts.disabled_user'))
      }
      return {
        disabledUser: {
          ...disabledUser,
          entityState: EntityState.Saved
        },
        newUserAuxList,
        windowId
      } as ChangeUserStatePayload
    } catch (error) {
      toast.error(i18next.t('errors.an_error_has_ocurred'))
      return thunkApi.rejectWithValue({
        message: 'errors.an_error_has_ocurred'
      } as ThunkErrorObject)
    }
  }
)

type ArrayElem<A> = A extends Array<infer Elem> ? Elem : never

export function elemT<T>(array: T): Array<ArrayElem<T>> {
  return array as any
}

export const saveUserData = createAsyncThunk(
  'apiUsers/saveUserData',
  async (
    {
      saveData,
      windowId
    }: { saveData: Partial<ApiSaveUserDataPostData>; windowId: string },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as State
      const response = await apiSaveUserData(
        {
          ...saveData,
          userToken: state.user.token
        },
        state.user.serverUrl
      )
      if (!response.data.ok) {
        toast.error(i18next.t('errors.an_error_has_ocurred'))
        return thunkApi.rejectWithValue({
          message: 'errors.an_error_has_ocurred'
        } as ThunkErrorObject)
      }
      const { userAuxList, ...savedData } = Object.entries(saveData).reduce<
        typeof saveData
      >((acum, [key, value]) => {
        if (typeof value === 'object') {
          return (acum = {
            ...acum,
            [key]: [
              ...elemT(value)
                .filter(
                  (currentValue) =>
                    currentValue.entityState !== EntityState.Deleted
                )
                .map((currentValue) => ({
                  ...currentValue,
                  entityState: EntityState.Saved
                }))
            ]
          })
        }
        return acum
      }, {})

      toast.success(i18next.t('alerts.user_saved'))

      thunkApi.dispatch(
        changeUserDetails({
          windowId,
          data: {
            ...savedData,
            ...(userAuxList != null && {
              userAux: userAuxList[0]
            })
          }
        })
      )
    } catch (error) {
      toast.error(i18next.t('errors.an_error_has_ocurred'))
      return thunkApi.rejectWithValue({
        message: 'errors.an_error_has_ocurred'
      } as ThunkErrorObject)
    }
  }
)

export const apiUsersSlice = createSlice({
  name: 'apiUsers',
  initialState,
  reducers: {
    removeApiUserState: (
      state,
      { payload }: PayloadAction<{ windowId: string }>
    ) => {
      state.ApiUsersStates = state.ApiUsersStates.filter(
        (apiUserState) => apiUserState.windowId !== payload.windowId
      )
    },
    changeTablePage: (
      state,
      {
        payload: { windowId, page }
      }: PayloadAction<{ windowId: string; page: number }>
    ) => {
      state.ApiUsersStates.find(
        (apiUserState) => apiUserState.windowId === windowId
      )!.tablePage = page
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      getUsers.pending,
      (
        state,
        {
          meta: {
            arg: { windowId }
          }
        }
      ) => {
        const apiUserState = state.ApiUsersStates.find(
          (ApiUser) => ApiUser.windowId === windowId
        )
        if (apiUserState != null) {
          apiUserState.loading = true
        } else {
          state.ApiUsersStates.push({
            loading: true,
            tablePage: 0,
            userAuxList: [],
            roleAuxList: [],
            userRoleAuxList: [],
            billingEntityAuxList: [],
            channelAuxList: [],
            environmentDataAuxList: [],
            hotelAuxList: [],
            languageAuxList: [],
            permissionAuxList: [],
            roomAuxList: [],
            roomTypeAuxList: [],
            zoneAuxList: [],
            windowId
          })
        }
      }
    )
    builder.addCase(
      getUsers.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { windowId }
          }
        }
      ) => {
        const apiUserStateId = state.ApiUsersStates.findIndex(
          (apiUser) => apiUser.windowId === windowId
        )
        const apiUserState = state.ApiUsersStates[
          apiUserStateId
        ] as ApiUserState

        if (apiUserState) {
          ;(Object.keys(payload) as Array<keyof ApiFetchUsersResponse>).map(
            (apiUserStateKey) => {
              ;(apiUserState[apiUserStateKey] as any) = payload[apiUserStateKey]
              return null
            }
          )
          apiUserState.loading = false
        }
      }
    )
    builder.addCase(changeUserStatus.fulfilled, (state, { payload }) => {
      const apiUserState = state.ApiUsersStates.find(
        (apiUserState) => apiUserState.windowId === payload?.windowId
      )
      if (apiUserState != null && payload?.newUserAuxList != null) {
        apiUserState.userAuxList = payload.newUserAuxList
      }
    })
    builder.addCase(
      saveUserData.fulfilled,
      (
        state,
        {
          meta: {
            arg: { windowId, saveData }
          }
        }
      ) => {
        if (saveData.userAuxList?.length && saveData.userAuxList?.length > 0) {
          const apiUserState = state.ApiUsersStates.find(
            (apiUsersState) => apiUsersState.windowId === windowId
          )
          if (apiUserState != null) {
            const userToChangeIndex = apiUserState?.userAuxList.findIndex(
              (userAux) => userAux.id === saveData.userAuxList![0].id
            )
            if (userToChangeIndex !== -1) {
              apiUserState.userAuxList[userToChangeIndex] = {
                ...saveData.userAuxList[0],
                entityState: EntityState.Saved
              }
            } else if (saveData.userAuxList?.length) {
              apiUserState.userAuxList = [
                ...saveData.userAuxList,
                ...apiUserState.userAuxList
              ]
            }
          }
        }
      }
    )
  }
})

export const { removeApiUserState, changeTablePage } = apiUsersSlice.actions

// {
//   "channelId": "FD172D1C59C04F019CB2C5B0E06707F4",
//   "entityState": "G",
//   "entityType": "userchannel_aux_list",
//   "id": "2DCC237349E24652937B3DC32AF2C3C3",
//   "userId": "BC76E2829735411F9A235A2BED21AF6C"
// }
